import { useQuery } from '@tanstack/react-query'

import type { BookingConfirmationSummary } from '../../types/booking-session.js'

import { apiClient } from '../../lib/client.js'
import { bookingSessionKeys } from './keys.js'

export interface UseBookingConfirmationSummaryOptions {
  /** Poll every 3s until a paid payment appears, then stop. Used on the
   *  pending/success pages so we react to the Axepta webhook settling — which
   *  also gates the GA4 `purchase` (fired on the effectively encashed amount). */
  pollUntilPaid?: boolean
  /** Polling interval in ms while the query is active. Useful for the SSP
   *  pending-payment page where we wait for the webhook to settle. */
  refetchInterval?: number
}

export const useBookingConfirmationSummary = (
  sessionId: string | undefined,
  options: UseBookingConfirmationSummaryOptions = {}
) =>
  useQuery({
    enabled: !!sessionId,
    queryFn: async () => {
      const response = await apiClient.client.get<BookingConfirmationSummary>(
        `/booking-session/${sessionId}/summary`
      )
      return response.data
    },
    queryKey: sessionId ? bookingSessionKeys.summary(sessionId) : bookingSessionKeys.all,
    refetchInterval: options.pollUntilPaid
      ? (query) => (query.state.data?.latestPaidPaymentId ? false : 3000)
      : options.refetchInterval,
    retry: false,
    staleTime: options.refetchInterval || options.pollUntilPaid ? 0 : 5 * 60 * 1000,
  })
